import { UserRole } from '../../enums/user-role';
import { UserToken } from './userToken';
import { GlobalConfig } from '@interfaces/global-config';
import { UserAvailabilityStatus } from '../../enums/user-availability-status';
import { CodeType } from '../../enums/code-type';

export type NotificationSettings = {
  popupNotifications: string[];
};

export class User {
  readonly id: string;
  readonly username: string;
  readonly inAppName: string;
  readonly token?: UserToken;
  readonly role: UserRole;
  readonly assignedCodes: CodeType[];
  readonly key: string;
  readonly ttl: string;
  readonly profilePicture: string;
  readonly globalConfiguration: GlobalConfig;
  readonly departments: string[];
  readonly availabilityStatus: UserAvailabilityStatus;
  readonly primaryDepartment?: string;
  readonly notificationSettings?: NotificationSettings;
  password?: string;

  constructor({
    id,
    inAppName,
    username,
    token,
    role,
    assignedCodes,
    key,
    ttl,
    globalConfiguration,
    availabilityStatus,
    profilePicture,
    password,
    departments,
    primaryDepartment,
    notificationSettings,
  }: {
    id: string;
    username: string;
    inAppName: string;
    profilePicture: string;
    token?: UserToken;
    role: UserRole;
    assignedCodes: CodeType[];
    key: string;
    ttl: string;
    globalConfiguration: GlobalConfig;
    availabilityStatus: UserAvailabilityStatus;
    password: string;
    departments: string[];
    primaryDepartment?: string;
    notificationSettings?: NotificationSettings;
  }) {
    this.id = id;
    this.username = username;
    this.inAppName = inAppName;
    this.token = token;
    this.role = role;
    this.assignedCodes = assignedCodes;
    this.profilePicture = profilePicture;
    this.key = key;
    this.ttl = ttl;
    this.globalConfiguration = globalConfiguration;
    this.availabilityStatus = availabilityStatus;
    this.password = password;
    this.departments = departments;
    this.primaryDepartment = primaryDepartment;
    this.notificationSettings = notificationSettings;
  }

  static builder() {
    return new UserBuilder();
  }
}

export class UserBuilder {
  static id: string;
  static username: string;
  static inAppName: string;
  static token?: UserToken;
  static role: UserRole;
  static key: string;
  static ttl: string;
  static globalConfiguration: GlobalConfig;
  static availabilityStatus: UserAvailabilityStatus;
  static profilePicture: string;
  static assignedCodes: CodeType[];
  static password: string;
  static departments: string[];
  static primaryDepartment: string;
  static notificationSettings?: NotificationSettings;

  static withNotificationSettings(notificationSettings: NotificationSettings) {
    this.notificationSettings = notificationSettings;
    return this;
  }

  static withTtl(ttl: string) {
    this.ttl = ttl;
    return this;
  }

  static withUserId(id: string) {
    this.id = id;
    return this;
  }

  static withRole(role: UserRole) {
    this.role = role;
    return this;
  }
  static withName(name: string) {
    this.inAppName = name;
    return this;
  }
  static withUsername(username: string) {
    this.username = username;
    return this;
  }

  static withToken(token?: UserToken) {
    this.token = token;
    return this;
  }

  static withKey(key: string) {
    this.key = key;
    return this;
  }

  static withAvailabilityStatus(availabilityStatus: UserAvailabilityStatus) {
    this.availabilityStatus = availabilityStatus;
    return this;
  }

  static withProfilePicture(profilePicture: string) {
    this.profilePicture = profilePicture;
    return this;
  }

  static withGlobalConfiguration(globalConfiguration: GlobalConfig) {
    this.globalConfiguration = globalConfiguration;
    return this;
  }

  static withDepartments(departments: string[]) {
    this.departments = departments;
    return this;
  }

  static withAssignedCodes(assignedCodes: CodeType[]) {
    this.assignedCodes = assignedCodes;
    return this;
  }

  static withPrimaryDepartment(primaryDepartment: string) {
    this.primaryDepartment = primaryDepartment;
    return this;
  }

  static build() {
    return new User({
      key: this.key,
      id: this.id,
      username: this.username,
      inAppName: this.inAppName,
      token: this.token,
      role: this.role,
      assignedCodes: this.assignedCodes,
      profilePicture: this.profilePicture,
      ttl: this.ttl,
      globalConfiguration: this.globalConfiguration,
      availabilityStatus: this.availabilityStatus,
      password: this.password,
      departments: this.departments,
      primaryDepartment: this.primaryDepartment,
      notificationSettings: this.notificationSettings,
    });
  }
}
